import React, { useState, useRef, useEffect } from "react";
import { trackEvent } from "../lib/analytics";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Hand, Mic, Square, Loader2 } from "lucide-react";
import { API } from "../lib/api";
import TurnstileChallenge from "./TurnstileChallenge";

const QUICK_PROMPTS = [
  "How does Senior Stylist work?",
  "How do I get started?",
  "What's included in a subscription?",
];

const getSessionId = () => {
  let id = localStorage.getItem("ss_chat_session");
  if (!id) {
    id = `sess_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    localStorage.setItem("ss_chat_session", id);
  }
  return id;
};

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hello, I am Chloe. I am front desk for Senior Stylist. Any questions, please ask.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [micError, setMicError] = useState("");
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mediaStreamRef = useRef(null);
  const [greet, setGreet] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());
  const turnstileRef = useRef(null);
  const sessionTok = useRef({ token: null, exp: 0 });
  const tsHeader = async () => {
    const token = await turnstileRef.current?.getToken();
    return token ? { "X-Turnstile-Token": token } : {};
  };
  // Reuse a short-lived session token when valid, otherwise solve Turnstile once.
  const authHeaders = async () => {
    if (sessionTok.current.token && sessionTok.current.exp - 20000 > Date.now()) {
      return { "X-Voice-Token": sessionTok.current.token };
    }
    return await tsHeader();
  };
  const storeToken = (token) => {
    if (token) sessionTok.current = { token, exp: Date.now() + 900000 };
  };

  useEffect(() => { if (open) trackEvent("chatbot_open"); }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (open) {
      setGreet(false);
      return;
    }
    const t = setTimeout(() => setGreet(true), 3500);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("open-stylist-chat", openHandler);
    return () => window.removeEventListener("open-stylist-chat", openHandler);
  }, []);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setBusy(true);

    let acc = "";
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeaders()) },
        body: JSON.stringify({ session_id: sessionId.current, message: content }),
      });
      if (!res.ok) {
        const friendly = res.status === 400 || res.status === 403
          ? "We could not verify your request just now. Please refresh the page and try again."
          : "Sorry, I'm having trouble connecting. Please try again.";
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: friendly };
          return copy;
        });
        return;
      }
      storeToken(res.headers.get("X-Voice-Token"));
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Sorry, I'm having trouble connecting. Please try again.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const stopTracks = () => {
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
  };

  const transcribe = async (blob) => {
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append("audio", blob, "speech.webm");
      const res = await fetch(`${API}/transcribe`, {
        method: "POST",
        headers: { ...(await authHeaders()) },
        body: form,
      });
      if (!res.ok) {
        setMicError("We could not transcribe that. Please try again.");
        return;
      }
      const data = await res.json();
      storeToken(data.voice_token);
      const text = (data.text || "").trim();
      if (text) {
        send(text);
      } else {
        setMicError("We did not catch that. Please try again.");
      }
    } catch (e) {
      setMicError("We could not transcribe that. Please try again.");
    } finally {
      setTranscribing(false);
    }
  };

  const toggleRecord = async () => {
    setMicError("");
    if (recording) {
      mediaRecorderRef.current?.stop();
      setRecording(false);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setMicError("Voice input is not supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stopTracks();
        const blob = new Blob(audioChunksRef.current, { type: mime || "audio/webm" });
        if (blob.size > 0) await transcribe(blob);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      trackEvent("chatbot_voice_record");
    } catch (e) {
      setMicError("We need microphone access to use voice input.");
      stopTracks();
    }
  };

  useEffect(() => () => {
    try { mediaRecorderRef.current?.stop(); } catch (_) {}
    stopTracks();
  }, []);

  return (
    <>
      <AnimatePresence>
        {greet && !open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.9 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[65] max-w-[240px]"
            data-testid="chatbot-greeting"
          >
            <div className="relative bg-white border border-stylist-border rounded-2xl rounded-br-sm shadow-xl px-4 py-3 pr-8">
              <button
                onClick={() => setGreet(false)}
                className="absolute top-2 right-2 text-stylist-muted hover:text-stylist-ink"
                aria-label="Dismiss"
                data-testid="chatbot-greeting-dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setOpen(true)} className="text-left flex items-start gap-3">
                <span className="w-10 h-10 rounded-full bg-stylist-bg2 border border-stylist-border flex items-center justify-center shrink-0">
                  <img src="/logo.png" alt="Senior Stylist" className="w-7 h-7 object-contain" />
                </span>
                <span className="block">
                  <span className="block text-sm font-medium text-stylist-ink">Hello, I am Chloe</span>
                  <span className="block text-xs text-stylist-ink2 mt-1 leading-snug">
                    I am front desk for Senior Stylist. Any questions, please ask.
                  </span>
                </span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chatbot-toggle-button"
        className={`fixed bottom-6 right-6 z-[65] w-16 h-16 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 ${
          open ? "bg-stylist-ink" : "bg-stylist-gold ss-pulse"
        }`}
        aria-label="Chat with our stylist"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="w-6 h-6 text-white" />
            </motion.span>
          ) : (
            <motion.span key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <Hand className="w-6 h-6 text-stylist-ink ss-wave" />
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[65] w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] max-h-[75vh] bg-[#FAF8F5] rounded-2xl shadow-2xl border border-stylist-border flex flex-col overflow-hidden"
            data-testid="chatbot-widget-container"
          >
            <div className="bg-stylist-ink px-5 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0">
                <img src="/logo.png" alt="Senior Stylist" className="w-7 h-7 object-contain" />
              </div>
              <div className="flex-1">
                <p className="text-white font-serif text-lg leading-none">Chloe</p>
                <p className="text-xs text-white/50 mt-1 font-mono">Senior Stylist · replies instantly</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-stylist-ink text-white rounded-2xl rounded-br-sm"
                        : "bg-white text-stylist-ink2 border border-stylist-border rounded-2xl rounded-bl-sm"
                    }`}
                  >
                    {m.content || (busy && i === messages.length - 1 ? <TypingDots /> : "")}
                  </div>
                </div>
              ))}

              {messages.length === 1 && (
                <div className="pt-2 space-y-2">
                  {QUICK_PROMPTS.map((p) => (
                    <button
                      key={p}
                      onClick={() => send(p)}
                      className="block w-full text-left text-sm px-4 py-2.5 rounded-xl bg-white border border-stylist-border text-stylist-ink2 hover:border-stylist-gold hover:text-stylist-ink transition-colors"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="p-3 border-t border-stylist-border bg-white flex items-center gap-2"
            >
              <button
                type="button"
                onClick={toggleRecord}
                disabled={busy || transcribing}
                data-testid="chatbot-mic-button"
                aria-label={recording ? "Stop recording" : "Record voice message"}
                title={recording ? "Tap to stop" : "Tap to speak"}
                className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors disabled:opacity-40 ${
                  recording
                    ? "bg-red-500 text-white ss-pulse"
                    : "bg-stylist-bg border border-stylist-border text-stylist-ink hover:border-stylist-gold hover:text-stylist-gold"
                }`}
              >
                {transcribing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : recording ? (
                  <Square className="w-4 h-4" />
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </button>
              <input
                data-testid="chatbot-message-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={recording ? "Listening, tap the square to stop…" : transcribing ? "Transcribing…" : "Ask us anything…"}
                className="flex-1 px-4 py-2.5 rounded-full bg-stylist-bg border border-stylist-border text-sm text-stylist-ink focus:outline-none focus:border-stylist-gold transition-colors"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="chatbot-send-button"
                className="w-10 h-10 rounded-full bg-stylist-ink text-white flex items-center justify-center hover:bg-stylist-gold transition-colors disabled:opacity-40 shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
            {micError && (
              <p className="px-4 pb-2 -mt-1 text-xs text-red-500 bg-white" data-testid="chatbot-mic-error">
                {micError}
              </p>
            )}
            <TurnstileChallenge ref={turnstileRef} action="chatbot" />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const TypingDots = () => (
  <span className="inline-flex gap-1 py-1">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="w-1.5 h-1.5 rounded-full bg-stylist-muted"
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
      />
    ))}
  </span>
);
